import React, { useMemo } from 'react';
import { Divider, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Duration } from 'luxon';
import { useMessages } from 'modules/intl';
import { DURATION_HH_MM_SS, formatTimeFromIsoString } from 'utils/date-time';
import { useFormattedDuration } from 'modules/notes/common';
import { DiaryTaskNameFormat } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { SmartDiaryEntry } from '../type';
import { SmartDiaryTooltip } from './SmartDiaryTooltip';

type LabelsTooltipProps = {
  smartEntry: SmartDiaryEntry;
  children: React.ReactElement;
};

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: theme.spacing(55)
  }
}));
export const LabelsTooltip: React.FC<LabelsTooltipProps> = ({
  smartEntry,
  children
}: LabelsTooltipProps) => {
  const classes = useStyles();
  const { popperPropsForTooltip } = useMemo(
    () => ({
      popperPropsForTooltip: {
        'data-testid': `tooltip_${smartEntry?.id}`
      } as any
    }),
    [smartEntry?.id]
  );

  const hasLabel = useMemo(
    () =>
      Boolean(
        smartEntry.client ||
          smartEntry.program ||
          smartEntry.project ||
          smartEntry.task ||
          smartEntry.billing ||
          smartEntry.activity ||
          (smartEntry.oefs?.length && smartEntry.oefs.some((oef) => oef.id)) ||
          smartEntry?.duration?.value
      ),
    [
      smartEntry.activity,
      smartEntry.billing,
      smartEntry.client,
      smartEntry?.duration?.value,
      smartEntry.oefs,
      smartEntry.program,
      smartEntry.project,
      smartEntry.task
    ]
  );

  const title = useMemo(() => <TooltipTitle smartEntry={smartEntry} />, [
    smartEntry
  ]);
  return (
    <Tooltip
      title={hasLabel ? title : ''}
      PopperProps={popperPropsForTooltip}
      classes={useMemo(() => ({ tooltip: classes.root }), [classes.root])}
      arrow
      describeChild={hasLabel ? true : false}
    >
      {children}
    </Tooltip>
  );
};

type TooltipTitleProp = {
  smartEntry: SmartDiaryEntry;
};

const useDividerStyles = makeStyles((theme) => ({
  divider: {
    backgroundColor: theme.palette.grey[500],
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}));

const messageIds = [
  'labels.client',
  'labels.project',
  'labels.task',
  'labels.totalDuration',
  'labels.startTime',
  'labels.endTime',
  'note.durationWithHours',
  'smartDiary.lastAppliedLabel',
  'smartDiary.neverSynced',
  'smartDiary.removedFromTimesheet',
  'smartDiary.notSelected',
  'labels.activity',
  'labels.billing',
  'labels.program'
];

const useTaskNameOrder = (
  entry: SmartDiaryEntry
): ('client' | 'project' | 'task' | 'program')[] =>
  useMemo(() => {
    switch (entry.taskNameFormat) {
      case DiaryTaskNameFormat.BoldTaskBreakProject:
        return ['task', 'project'];
      case DiaryTaskNameFormat.BoldTaskBreakClientProject:
        return ['task', 'client', 'project'];
      case DiaryTaskNameFormat.BoldTaskBreakProgramProject:
        return ['task', 'program', 'project'];
      case DiaryTaskNameFormat.BoldProjectBreakTask:
      case DiaryTaskNameFormat.ProjectBreakBoldTask:
      case DiaryTaskNameFormat.BoldProjectTask:
      case DiaryTaskNameFormat.ProjectTask:
      case DiaryTaskNameFormat.ProjectBoldTask:
        return ['project', 'task'];
      case DiaryTaskNameFormat.ClientBreakBoldProjectTask:
      case DiaryTaskNameFormat.BoldClientBreakProjectTask:
      case DiaryTaskNameFormat.ClientProjectBoldTask:
      case DiaryTaskNameFormat.ClientProjectBreakBoldTask:
        return ['client', 'project', 'task'];
      case DiaryTaskNameFormat.ProgramBreakBoldProjectTask:
      case DiaryTaskNameFormat.BoldProgramBreakProjectTask:
      case DiaryTaskNameFormat.ProgramProjectBoldTask:
      case DiaryTaskNameFormat.ProgramProjectBreakBoldTask:
        return ['program', 'project', 'task'];
      default:
        return entry.client
          ? ['client', 'project', 'task']
          : ['program', 'project', 'task'];
    }
  }, [entry.client, entry.taskNameFormat]);

const TooltipTitle: React.FC<TooltipTitleProp> = ({
  smartEntry
}: TooltipTitleProp) => {
  const dividerStyleClasses = useDividerStyles();
  const messageValue = useMemo(
    () => ({
      duration: Duration.fromObject({
        seconds: smartEntry?.duration?.value ?? 0
      }).toFormat(DURATION_HH_MM_SS)
    }),
    [smartEntry?.duration?.value]
  );
  const messages = useMessages(messageIds, messageValue);
  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: smartEntry?.duration?.value ?? 0
  });
  const cptOrder = useTaskNameOrder(smartEntry);
  const me = useMeContext();
  const hasStartEndTime = useMemo(
    () => Boolean(smartEntry?.startTime && smartEntry?.endTime),
    [smartEntry?.endTime, smartEntry?.startTime]
  );
  const hasDuration = useMemo(
    () =>
      Boolean(
        smartEntry?.duration?.value !== null &&
          smartEntry?.duration?.value !== undefined
      ),

    [smartEntry?.duration?.value]
  );
  const timeToolTip = useMemo(() => {
    if (hasStartEndTime)
      return (
        <>
          <SmartDiaryTooltip
            id={`tooltip_key_duration_${smartEntry.id}`}
            name={messages['labels.startTime']}
            value={formatTimeFromIsoString(smartEntry?.startTime ?? '', me)}
          ></SmartDiaryTooltip>
          <SmartDiaryTooltip
            id={`tooltip_key_duration_${smartEntry.id}`}
            name={messages['labels.endTime']}
            value={formatTimeFromIsoString(smartEntry?.endTime ?? '', me)}
          ></SmartDiaryTooltip>
        </>
      );
    else {
      if (hasDuration)
        return (
          <SmartDiaryTooltip
            id={`tooltip_key_duration_${smartEntry.id}`}
            name={messages['labels.totalDuration']}
            value={formattedDuration}
          ></SmartDiaryTooltip>
        );
      else return <></>;
    }
  }, [
    formattedDuration,
    hasDuration,
    hasStartEndTime,
    me,
    messages,
    smartEntry?.endTime,
    smartEntry.id,
    smartEntry?.startTime
  ]);

  return (
    <div>
      {cptOrder.map((key) => (
        <SmartDiaryTooltip
          key={`tooltip_key_${smartEntry.id}_${key}_${smartEntry[key]?.id}`}
          id={`tooltip_key_${smartEntry.id}_${smartEntry[key]?.id}`}
          name={messages[`labels.${key}`]}
          value={smartEntry[key]?.value ?? messages['smartDiary.notSelected']}
        />
      ))}

      {smartEntry.billing && (
        <SmartDiaryTooltip
          id={`tooltip_key_billing_${smartEntry.id}_${smartEntry.billing.id}`}
          name={messages['labels.billing']}
          value={
            smartEntry?.billing.value ?? messages['smartDiary.notSelected']
          }
        />
      )}

      {smartEntry.activity && (
        <SmartDiaryTooltip
          id={`tooltip_key_activity_${smartEntry.id}_${smartEntry.activity.id}`}
          name={messages['labels.activity']}
          value={
            smartEntry?.activity.value ?? messages['smartDiary.notSelected']
          }
        />
      )}

      {(smartEntry.client ||
        smartEntry.project ||
        smartEntry.task ||
        smartEntry.billing ||
        smartEntry.activity) &&
        ((smartEntry.oefs ?? []).some((oef) => oef.id) ||
          Boolean(smartEntry.duration?.value)) && (
          <Divider
            data-testid={`label_tooltip_divider_${smartEntry.id}_1`}
            classes={{ root: dividerStyleClasses.divider }}
          />
        )}
      <>
        {(smartEntry.oefs ?? []).map((oef) => {
          return (
            oef.id && (
              <div key={`tooltip_key_oef_${smartEntry.id}_${oef.id}`}>
                <SmartDiaryTooltip
                  id={`tooltip_key_${smartEntry.id}_${oef.id}`}
                  name={oef.name ?? ''}
                  value={oef.value ?? ''}
                ></SmartDiaryTooltip>
              </div>
            )
          );
        })}
        {<div>{timeToolTip}</div>}
      </>
      {
        <>
          <Divider
            data-testid={`label_tooltip_divider_${smartEntry.id}_2`}
            classes={{ root: dividerStyleClasses.divider }}
          />
          <SmartDiaryTooltip
            id={`tooltip_key_${smartEntry.id}_lastSyncTime`}
            name={messages['smartDiary.lastAppliedLabel']}
            value={
              smartEntry.lastSyncTime
                ? smartEntry.lastSyncTime.toFormat('MMM d, yyyy h:mm a')
                : messages['smartDiary.neverSynced']
            }
          />
        </>
      }
      {!(smartEntry.timeSignals ?? []).length && (
        <>
          <Divider
            data-testid={`label_tooltip_divider_${smartEntry.id}_3`}
            classes={{ root: dividerStyleClasses.divider }}
          />
          <SmartDiaryTooltip
            id={`tooltip_key_${smartEntry.id}_removedFromTI`}
            value={messages['smartDiary.removedFromTimesheet']}
          />
        </>
      )}
    </div>
  );
};
